(() => {
  const GARAGE_KEY = 'samsunsPartsFinderGarageWebV1';
  const LEGACY_GARAGE_KEY = 'samsunsPartsFinderGarageV1';
  const MAX_FILE_BYTES = 4 * 1024 * 1024;
  const SUPPORTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

  class SamsunsPartsFinder {
    constructor(root) {
      this.root = root;
      this.apiRoot = root.dataset.apiRoot;
      this.pendingVehicle = null;
      this.vehicle = null;
      this.selectedGroup = null;
      this.ocrEngineCodeHint = null;
      this.ocrPendingConfirmation = false;
      this.uncertainOcrFields = new Set();
      this.ocrRequestVersion = 0;
      this.currentStep = 1;
      this.maxReachedStep = 1;
      this.hasSearchResult = false;
      this.busy = false;
      this.elements = this.collectElements();
      this.bindEvents();
      this.restoreGarage();
      this.updateVehicleButton();
      this.restoreContactSubmissionState();
    }

    collectElements() {
      const get = (selector) => this.root.querySelector(selector);
      return {
        hsn: get('[data-spf-hsn]'),
        tsn: get('[data-spf-tsn]'),
        engineCode: get('[data-spf-engine-code]'),
        directPartQuery: get('[data-spf-direct-part-query]'),
        directPartSearch: get('[data-spf-direct-part-search]'),
        upload: get('[data-spf-upload]'),
        file: get('[data-spf-file]'),
        selectedFile: get('[data-spf-selected-file]'),
        fileName: get('[data-spf-file-name]'),
        removeFile: get('[data-spf-remove-file]'),
        readDocument: get('[data-spf-read-document]'),
        vehicleSubmit: get('[data-spf-vehicle-submit]'),
        vehicleMessage: get('[data-spf-message]'),
        confirmationNotice: get('[data-spf-confirmation-notice]'),
        vehicleStep: get('[data-spf-step="vehicle"]'),
        confirmationStep: get('[data-spf-step="confirmation"]'),
        confirmationSubmit: get('[data-spf-confirm-vehicle]'),
        confirmationMessage: get('[data-spf-confirmation-message]'),
        editVehicle: get('[data-spf-edit-vehicle]'),
        confirmVehicleName: get('[data-spf-confirm-vehicle-name]'),
        confirmVehicleSubtitle: get('[data-spf-confirm-vehicle-subtitle]'),
        confirmEngine: get('[data-spf-confirm-engine]'),
        confirmPower: get('[data-spf-confirm-power]'),
        confirmFuel: get('[data-spf-confirm-fuel]'),
        confirmBody: get('[data-spf-confirm-body]'),
        confirmPeriod: get('[data-spf-confirm-period]'),
        confirmKba: get('[data-spf-confirm-kba]'),
        partStep: get('[data-spf-step="part"]'),
        resultsStep: get('[data-spf-step="results"]'),
        partVehicleName: get('[data-spf-part-vehicle-name]'),
        partQuery: get('[data-spf-part-query]'),
        categorySearch: get('[data-spf-category-search]'),
        categoryResults: get('[data-spf-category-results]'),
        categoryList: get('[data-spf-category-list]'),
        partMessage: get('[data-spf-part-message]'),
        productsSubmit: get('[data-spf-products-submit]'),
        productGrid: get('[data-spf-product-grid]'),
        resultsMessage: get('[data-spf-results-message]'),
        contactCard: get('[data-spf-contact-card]'),
        contactVehicle: get('[data-spf-contact-vehicle]'),
        contactPart: get('[data-spf-contact-part]'),
        contactMessage: get('[data-spf-contact-message]'),
        garage: get('[data-spf-garage]'),
        garageToggle: get('[data-spf-garage-toggle]'),
        garageName: get('[data-spf-garage-name]'),
        garageMeta: get('[data-spf-garage-meta]'),
        useGarage: get('[data-spf-use-garage]'),
        removeGarage: get('[data-spf-remove-garage]'),
        changeVehicle: get('[data-spf-change-vehicle]'),
        newSearch: get('[data-spf-new-search]'),
        progressItems: Array.from(this.root.querySelectorAll('[data-spf-progress]')),
        progressLines: Array.from(this.root.querySelectorAll('[data-spf-progress-line]'))
      };
    }

    bindEvents() {
      this.root.querySelectorAll('[data-spf-whatsapp], [data-spf-whatsapp-vin]').forEach((link) => {
        link.addEventListener('click', (event) => {
          const number = String(this.root.dataset.whatsappNumber || '').replace(/\D/g, '');
          const withVin = link.hasAttribute('data-spf-whatsapp-vin');
          const vinInput = this.root.querySelector('[data-spf-vin]');
          const vin = (vinInput?.value || '').trim().toUpperCase();
          const vinMessage = this.root.querySelector('[data-spf-vin-message]');
          if (withVin && !/^[A-HJ-NPR-Z0-9]{17}$/.test(vin)) {
            event.preventDefault();
            this.showMessage(vinMessage, 'Bitte geben Sie eine FIN mit 17 Zeichen ein (ohne I, O oder Q).');
            vinInput?.focus();
            return;
          }
          if (!/^\d{8,15}$/.test(number)) {
            event.preventDefault();
            return;
          }
          if (withVin) this.clearMessage(vinMessage);
          const lines = ['Hallo Samsuns Group, ich benötige Hilfe bei der Fahrzeug- oder Teilezuordnung.'];
          if (withVin) lines.push(`FIN / VIN: ${vin}`);
          if (this.elements.hsn.value.trim()) lines.push(`HSN: ${this.elements.hsn.value.trim()}`);
          if (this.elements.tsn.value.trim()) lines.push(`TSN: ${this.elements.tsn.value.trim()}`);
          if (this.vehicle?.resolved) lines.push(`Fahrzeug: ${this.vehicle.resolved.manufacturer} ${this.vehicle.resolved.model}`);
          if (this.elements.partQuery.value.trim()) lines.push(`Gesuchtes Ersatzteil: ${this.elements.partQuery.value.trim()}`);
          link.href = `https://wa.me/${number}?text=${encodeURIComponent(lines.join('\n'))}`;
        });
      });
      this.elements.file.addEventListener('change', () => this.handleSelectedFile());
      this.elements.removeFile.addEventListener('click', () => this.removeSelectedFile());
      this.elements.readDocument.addEventListener('click', () => {
        const file = this.elements.file.files[0];
        if (file) this.readDocument(file);
      });

      let dragDepth = 0;
      ['dragenter', 'dragover', 'dragleave', 'drop'].forEach((eventName) => {
        this.elements.upload.addEventListener(eventName, (event) => {
          event.preventDefault();
          event.stopPropagation();
        });
      });
      this.elements.upload.addEventListener('dragenter', () => {
        dragDepth += 1;
        this.elements.upload.classList.add('is-dragging');
      });
      this.elements.upload.addEventListener('dragover', (event) => {
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
      });
      this.elements.upload.addEventListener('dragleave', () => {
        dragDepth = Math.max(0, dragDepth - 1);
        if (dragDepth === 0) this.elements.upload.classList.remove('is-dragging');
      });
      this.elements.upload.addEventListener('drop', (event) => {
        dragDepth = 0;
        this.elements.upload.classList.remove('is-dragging');
        const droppedFile = event.dataTransfer?.files?.[0];
        if (!droppedFile) return;

        const transfer = new DataTransfer();
        transfer.items.add(droppedFile);
        this.elements.file.files = transfer.files;
        this.handleSelectedFile();
      });

      [
        this.elements.hsn,
        this.elements.tsn,
        this.elements.engineCode
      ].forEach((input) => {
        input.addEventListener('input', () => this.handleVehicleFieldInput(input));
      });

      this.elements.progressItems.forEach((button) => {
        button.addEventListener('click', () => {
          this.navigateToStep(Number(button.dataset.spfProgress));
        });
      });
      this.elements.vehicleSubmit.addEventListener('click', () => this.resolveVehicle());
      this.elements.directPartSearch.addEventListener('click', () => {
        this.searchShopifyByPartNumber();
      });
      this.elements.directPartQuery.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.searchShopifyByPartNumber();
        }
      });
      this.elements.directPartQuery.addEventListener('input', () => {
        this.elements.directPartQuery.setCustomValidity('');
      });
      this.elements.confirmationSubmit.addEventListener('click', () => this.confirmVehicle());
      this.elements.editVehicle.addEventListener('click', () => this.editVerifiedVehicle());
      this.elements.categorySearch.addEventListener('click', () => this.searchCategories());
      this.elements.partQuery.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.searchCategories();
        }
      });
      this.root.querySelectorAll('[data-spf-suggestion]').forEach((button) => {
        button.addEventListener('click', () => {
          this.elements.partQuery.value = button.dataset.spfSuggestion;
          this.searchCategories();
        });
      });
      this.elements.productsSubmit.addEventListener('click', () => this.searchProducts());
      this.elements.changeVehicle.addEventListener('click', () => this.editVerifiedVehicle());
      this.elements.newSearch.addEventListener('click', () => this.resetPartSearch());
      this.elements.useGarage.addEventListener('click', () => this.useGarage());
      this.elements.removeGarage.addEventListener('click', () => this.removeGarage());
      this.elements.garageToggle.addEventListener('click', () => {
        this.elements.garage.hidden = !this.elements.garage.hidden;
      });
    }

    handleVehicleFieldInput(input) {
      this.ocrRequestVersion += 1;
      input.value = input.value.toUpperCase().replace(/\s+/g, '');
      const field = this.vehicleFieldName(input);
      if (field && this.uncertainOcrFields.delete(field)) {
        input.removeAttribute('aria-invalid');
        input.closest('.spf-field')?.classList.remove('is-uncertain');
      }
      if (input === this.elements.engineCode) {
        this.ocrEngineCodeHint = input.value || null;
      }
      if (this.pendingVehicle || this.vehicle) {
        this.pendingVehicle = null;
        this.vehicle = null;
        this.selectedGroup = null;
        this.hasSearchResult = false;
        this.clearDownstreamResults();
      }
      this.maxReachedStep = 1;
      this.currentStep = Math.min(this.currentStep, this.maxReachedStep);
      this.updateVehicleButton();
    }

    vehicleFieldName(input) {
      if (input === this.elements.hsn) return 'hsn';
      if (input === this.elements.tsn) return 'tsn';
      if (input === this.elements.engineCode) return 'engineCode';
      return null;
    }

    vehicleInput(field) {
      const inputs = {
        hsn: this.elements.hsn,
        tsn: this.elements.tsn,
        engineCode: this.elements.engineCode
      };
      return inputs[field] || null;
    }

    clearOcrUncertainty() {
      this.uncertainOcrFields.clear();
      ['hsn', 'tsn', 'engineCode'].forEach((field) => {
        const input = this.vehicleInput(field);
        input?.removeAttribute('aria-invalid');
        input?.closest('.spf-field')?.classList.remove('is-uncertain');
      });
    }

    applyOcrUncertainty(fields) {
      this.clearOcrUncertainty();
      fields.forEach((field) => {
        const input = this.vehicleInput(field);
        if (!input) return;
        this.uncertainOcrFields.add(field);
        input.setAttribute('aria-invalid', 'true');
        input.closest('.spf-field')?.classList.add('is-uncertain');
      });
    }

    replaceVehicleInputSnapshot(vehicle = {}) {
      this.elements.hsn.value = vehicle.hsn || '';
      this.elements.tsn.value = vehicle.tsn || '';
      this.elements.engineCode.value = vehicle.engineCode || '';
      this.ocrEngineCodeHint = vehicle.engineCode || null;
    }

    resetVehicleResolutionState() {
      this.pendingVehicle = null;
      this.vehicle = null;
      this.selectedGroup = null;
      this.hasSearchResult = false;
      this.clearDownstreamResults();
      this.maxReachedStep = 1;
      this.currentStep = 1;
    }

    clearDownstreamResults() {
      this.elements.partQuery.value = '';
      this.elements.categoryList.replaceChildren();
      this.elements.categoryResults.hidden = true;
      this.elements.productsSubmit.hidden = true;
      this.elements.productGrid.replaceChildren();
      if (this.elements.contactCard) this.elements.contactCard.hidden = true;
      this.clearMessage(this.elements.partMessage);
      this.clearMessage(this.elements.resultsMessage);
    }

    handleSelectedFile() {
      this.ocrRequestVersion += 1;
      this.ocrPendingConfirmation = false;
      this.clearOcrUncertainty();
      this.elements.confirmationNotice.hidden = true;
      const file = this.elements.file.files[0];
      this.elements.fileName.textContent = file?.name || '';
      this.elements.selectedFile.hidden = !file;
      this.clearMessage(this.elements.vehicleMessage);

      if (file && !SUPPORTED_TYPES.includes(file.type)) {
        this.showMessage(this.elements.vehicleMessage, 'Bitte laden Sie ein JPG-, PNG- oder WebP-Foto hoch.');
      } else if (file && file.size > MAX_FILE_BYTES) {
        this.showMessage(this.elements.vehicleMessage, 'Das Foto darf maximal 4 MB groß sein.');
      }
      this.updateVehicleButton();
    }

    removeSelectedFile() {
      this.ocrRequestVersion += 1;
      this.elements.file.value = '';
      this.ocrPendingConfirmation = false;
      this.clearOcrUncertainty();
      this.elements.selectedFile.hidden = true;
      this.elements.fileName.textContent = '';
      this.elements.confirmationNotice.hidden = true;
      this.clearMessage(this.elements.vehicleMessage);
      this.updateVehicleButton();
    }

    hasVehicleIdentifierInput() {
      const hsn = this.elements.hsn.value.trim().toUpperCase();
      const tsn = this.elements.tsn.value.trim().toUpperCase();
      const trustedKba =
        hsn &&
        tsn &&
        !this.uncertainOcrFields.has('hsn') &&
        !this.uncertainOcrFields.has('tsn');
      return Boolean(trustedKba);
    }

    searchShopifyByPartNumber() {
      const query = this.elements.directPartQuery.value.trim();
      if (!query) {
        this.elements.directPartQuery.setCustomValidity(
          'Bitte geben Sie eine OE- oder Teilenummer ein.'
        );
        this.elements.directPartQuery.reportValidity();
        this.elements.directPartQuery.focus();
        return;
      }

      const searchUrl = new URL(
        this.root.dataset.searchUrl || '/search',
        window.location.origin
      );
      searchUrl.searchParams.set('q', query);
      searchUrl.searchParams.set('type', 'product');
      window.location.assign(searchUrl.toString());
    }

    updateVehicleButton() {
      const file = this.elements.file.files[0];
      const validFile =
        file &&
        SUPPORTED_TYPES.includes(file.type) &&
        file.size <= MAX_FILE_BYTES;
      this.elements.readDocument.disabled = this.busy || !validFile;
      this.elements.vehicleSubmit.disabled =
        this.busy || !this.hasVehicleIdentifierInput();
      this.elements.confirmationSubmit.disabled = this.busy;
      this.syncProgress();
    }

    setBusy(button, busy, label) {
      this.busy = busy;
      button.disabled = busy;
      if (busy) {
        button.dataset.originalLabel = button.textContent;
        button.textContent = label;
      } else if (button.dataset.originalLabel) {
        button.textContent = button.dataset.originalLabel;
        delete button.dataset.originalLabel;
      }
      this.updateVehicleButton();
    }

    showMessage(element, message, type = 'error') {
      element.textContent = message;
      element.classList.toggle('is-info', type === 'info');
      element.hidden = false;
    }

    clearMessage(element) {
      element.hidden = true;
      element.textContent = '';
      element.classList.remove('is-info');
    }

    async api(path, body) {
      const response = await fetch(`${this.apiRoot}${path}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body)
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const status = payload.status || payload.error || 'request_failed';
        const error = new Error(path === '/ocr' ? this.localizedApiMessage(status) : (payload.message || this.localizedApiMessage(status)));
        error.status = status;
        throw error;
      }
      return payload;
    }

    localizedApiMessage(status) {
      const messages = {
        image_too_large: 'Das Foto darf maximal 4 MB groß sein.',
        ocr_unavailable:
          'Der Fahrzeugschein kann momentan nicht gelesen werden. Bitte versuchen Sie es erneut oder geben Sie die Fahrzeugdaten manuell ein.',
        confirmation_required:
          'Bitte prüfen Sie die Fahrzeugdaten und bestätigen Sie die Angaben.',
        no_verified_vehicle:
          'Das Fahrzeug konnte nicht gefunden werden. Bitte prüfen Sie die eingegebenen Fahrzeugdaten.',
        ambiguous_vehicle:
          'Das Fahrzeug konnte nicht eindeutig identifiziert werden. Bitte ergänzen oder prüfen Sie die Fahrzeugdaten.',
        no_verified_category:
          'In der verfügbaren Datenquelle wurde kein OE-Treffer gefunden. Sie können eine Teileanfrage senden.',
        no_tecdoc_match:
          'In der verfügbaren Datenquelle wurde kein OE-Treffer gefunden. Sie können eine Teileanfrage senden.',
        no_shopify_match:
          'In der verfügbaren Datenquelle wurde kein OE-Treffer gefunden. Sie können eine Teileanfrage senden.',
        invalid_request:
          'Bitte prüfen Sie Ihre Eingaben und versuchen Sie es erneut.',
        service_unavailable:
          'Der Service ist vorübergehend nicht verfügbar. Bitte versuchen Sie es später erneut.',
        unauthorized:
          'Die Anfrage konnte nicht abgeschlossen werden. Bitte laden Sie die Seite neu.',
        not_found:
          'Die Anfrage konnte nicht abgeschlossen werden. Bitte laden Sie die Seite neu.'
      };
      return messages[status] ||
        'Die Anfrage konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut.';
    }

    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('Das Foto konnte nicht gelesen werden.'));
        reader.onload = () => {
          const result = String(reader.result || '');
          resolve(result.includes(',') ? result.split(',')[1] : result);
        };
        reader.readAsDataURL(file);
      });
    }

    async readDocument(file) {
      if (!SUPPORTED_TYPES.includes(file.type)) {
        this.showMessage(this.elements.vehicleMessage, 'Bitte laden Sie ein JPG-, PNG- oder WebP-Foto hoch.');
        return;
      }
      if (file.size > MAX_FILE_BYTES) {
        this.showMessage(this.elements.vehicleMessage, 'Das Foto darf maximal 4 MB groß sein.');
        return;
      }

      const requestVersion = ++this.ocrRequestVersion;
      this.ocrPendingConfirmation = false;
      this.clearOcrUncertainty();
      this.replaceVehicleInputSnapshot({});
      this.resetVehicleResolutionState();
      this.elements.confirmationNotice.hidden = true;
      this.clearMessage(this.elements.vehicleMessage);
      this.setBusy(this.elements.readDocument, true, 'Fahrzeugschein wird gelesen …');
      try {
        const imageBase64 = await this.fileToBase64(file);
        const result = await this.api('/ocr', { imageBase64, mimeType: file.type });
        if (requestVersion !== this.ocrRequestVersion) return;
        if (result.status !== 'needs_confirmation') {
          throw new Error('Bitte laden Sie ein klares Foto des Fahrzeugscheins hoch oder geben Sie die Fahrzeugdaten manuell ein.');
        }
        this.replaceVehicleInputSnapshot(result.vehicle);
        const uncertaintyFields = (result.uncertainFields || []).filter(
          (field) => field !== 'engineCode' || Boolean(result.vehicle.engineCode)
        );
        this.applyOcrUncertainty(uncertaintyFields);
        this.ocrPendingConfirmation = true;
        this.elements.confirmationNotice.hidden = false;
        const hasRequiredFieldUncertainty =
          this.uncertainOcrFields.has('hsn') ||
          this.uncertainOcrFields.has('tsn');
        if (hasRequiredFieldUncertainty) {
          this.showMessage(
            this.elements.vehicleMessage,
            'Ein oder mehrere Fahrzeugdaten konnten nicht sicher gelesen werden. Bitte laden Sie ein klareres Foto hoch oder prüfen und korrigieren Sie die markierten Felder manuell.'
          );
        } else {
          this.showMessage(
            this.elements.vehicleMessage,
            'Bitte vergleichen Sie die ausgelesenen Werte mit Ihrem Fahrzeugschein.',
            'info'
          );
        }
      } catch (error) {
        if (requestVersion !== this.ocrRequestVersion) return;
        this.showMessage(
          this.elements.vehicleMessage,
          error.message || 'Bitte laden Sie ein klares Foto des Fahrzeugscheins hoch.'
        );
      } finally {
        this.setBusy(this.elements.readDocument, false);
        this.updateVehicleButton();
      }
    }

    async resolveVehicle() {
      if (!this.hasVehicleIdentifierInput()) {
        this.showMessage(
          this.elements.vehicleMessage,
          'Bitte geben Sie HSN und TSN vollständig ein.'
        );
        return;
      }

      const hsn = this.elements.hsn.value.trim().toUpperCase();
      const tsn = this.elements.tsn.value.trim().toUpperCase();
      const engineCode = this.elements.engineCode.value.trim().toUpperCase();
      const trustedKba =
        !this.uncertainOcrFields.has('hsn') &&
        !this.uncertainOcrFields.has('tsn') &&
        hsn &&
        tsn;
      const trustedEngineCode = this.uncertainOcrFields.has('engineCode')
        ? null
        : engineCode || null;
      this.clearMessage(this.elements.vehicleMessage);
      this.setBusy(this.elements.vehicleSubmit, true, 'Fahrzeug wird identifiziert …');
      try {
        const result = await this.api('/vehicle/resolve', {
          confirmed: true,
          hsn: trustedKba ? hsn : null,
          tsn: trustedKba ? tsn : null,
          engineCode: trustedEngineCode
        });
        const officialEngineCodes =
          result.vehicle.engineCodes?.filter(Boolean) || [];
        const enrichedEngineCode =
          trustedEngineCode ||
          (officialEngineCodes.length === 1
            ? officialEngineCodes[0]
            : null);
        this.pendingVehicle = {
          hsn: trustedKba ? hsn : null,
          tsn: trustedKba ? tsn : null,
          tsnLookup: trustedKba ? tsn.slice(0, 3) : null,
          engineCode: enrichedEngineCode,
          resolved: result.vehicle
        };
        this.selectedGroup = null;
        this.hasSearchResult = false;
        this.clearDownstreamResults();
        this.openConfirmationStep();
      } catch (error) {
        this.showMessage(
          this.elements.vehicleMessage,
          error.message || 'Das Fahrzeug konnte nicht eindeutig identifiziert werden.'
        );
      } finally {
        this.setBusy(this.elements.vehicleSubmit, false);
        this.updateVehicleButton();
      }
    }

    openConfirmationStep() {
      const candidate = this.pendingVehicle || this.vehicle;
      if (!candidate?.resolved) return;

      const vehicle = candidate.resolved;
      this.elements.confirmVehicleName.textContent =
        `${vehicle.manufacturer} ${vehicle.model}`;
      this.elements.confirmVehicleSubtitle.textContent = [
        vehicle.engine,
        vehicle.bodyType
      ].filter(Boolean).join(' · ');
      this.elements.confirmEngine.textContent =
        vehicle.engine || 'Nicht angegeben';
      this.elements.confirmPower.textContent = this.formatPower(
        vehicle.powerKw,
        vehicle.powerPs
      );
      this.elements.confirmFuel.textContent =
        vehicle.fuelType || 'Nicht angegeben';
      this.elements.confirmBody.textContent =
        vehicle.bodyType || 'Nicht angegeben';
      this.elements.confirmPeriod.textContent = vehicle.sourcePeriod || this.formatVehiclePeriod(
        vehicle.constructionIntervalStart,
        vehicle.constructionIntervalEnd
      );
      this.elements.confirmKba.textContent =
        candidate.hsn && candidate.tsn
          ? `${candidate.hsn} / ${candidate.tsn}`
          : 'Nicht angegeben';
      this.clearMessage(this.elements.confirmationMessage);
      this.elements.vehicleStep.hidden = true;
      this.elements.partStep.hidden = true;
      this.elements.resultsStep.hidden = true;
      this.elements.confirmationStep.hidden = false;
      this.setProgress(2);
      this.elements.confirmationSubmit.focus();
    }

    confirmVehicle() {
      const candidate = this.pendingVehicle || this.vehicle;
      if (!candidate?.resolved) {
        this.showMessage(
          this.elements.confirmationMessage,
          'Bitte identifizieren Sie das Fahrzeug erneut.'
        );
        return;
      }

      this.vehicle = candidate;
      this.pendingVehicle = null;
      this.saveGarage();
      this.openPartStep();
    }

    formatPower(powerKw, powerPs) {
      const kw = Number(powerKw);
      const ps = Number(powerPs);
      const values = [];
      if (Number.isFinite(kw) && kw > 0) values.push(`${Math.round(kw)} kW`);
      if (Number.isFinite(ps) && ps > 0) {
        values.push(`${Math.round(ps)} PS`);
      } else if (Number.isFinite(kw) && kw > 0) {
        values.push(`${Math.round(kw * 1.35962)} PS`);
      }
      return values.join(' / ') || 'Nicht angegeben';
    }

    formatVehiclePeriod(start, end) {
      const format = (value) => {
        const digits = String(value || '').replace(/\D/g, '');
        if (digits.length >= 6) {
          return `${digits.slice(4, 6)}/${digits.slice(0, 4)}`;
        }
        return value || '';
      };
      const from = format(start);
      const to = format(end);
      if (from && to) return `${from} bis ${to}`;
      if (from) return `ab ${from}`;
      return 'Nicht angegeben';
    }

    openPartStep({ focus = true } = {}) {
      const vehicle = this.vehicle.resolved;
      this.elements.partVehicleName.textContent =
        `${vehicle.manufacturer} ${vehicle.model}`;
      this.elements.vehicleStep.hidden = true;
      this.elements.confirmationStep.hidden = true;
      this.elements.resultsStep.hidden = true;
      this.elements.partStep.hidden = false;
      this.setProgress(3);
      if (focus) this.elements.partQuery.focus();
    }

    async searchCategories() {
      if (this.busy || !this.vehicle) return;
      const query = this.elements.partQuery.value.trim();
      this.clearMessage(this.elements.partMessage);
      if (query.length < 2) {
        this.showMessage(this.elements.partMessage, 'Bitte geben Sie mindestens zwei Zeichen ein.');
        return;
      }

      this.selectedGroup = null;
      this.hasSearchResult = false;
      this.maxReachedStep = 3;
      this.syncProgress();
      this.elements.productsSubmit.hidden = true;
      this.elements.categoryResults.hidden = true;
      this.elements.categoryList.replaceChildren();
      this.setBusy(this.elements.categorySearch, true, 'Suche …');
      try {
        const result = await this.api('/categories/search', {
          vehicleId: this.vehicle.resolved.vehicleId,
          query
        });
        if (!result.groups.length) {
          this.renderProducts([]);
          this.showMessage(this.elements.resultsMessage, result.message || 'In der Datenquelle wurden keine Teilekandidaten gefunden.', 'info');
          return;
        }
        this.showMessage(this.elements.partMessage, result.message || 'Bitte wählen Sie eine Teilegruppe aus.', 'info');
        result.groups.forEach((group) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'spf-category-option';
          button.setAttribute('aria-pressed', 'false');
          const label = document.createElement('strong');
          label.textContent = this.localizedPartName(group.label);
          const state = document.createElement('span');
          state.className = 'spf-category-state';
          state.textContent = 'Auswählen';
          button.append(label, state);
          button.addEventListener('click', () => this.selectGroup(group, button));
          this.elements.categoryList.append(button);
        });
        this.elements.categoryResults.hidden = false;
      } catch (error) {
        if (error.status === 'no_verified_category') {
          this.renderProducts([]);
          this.showMessage(
            this.elements.resultsMessage,
            'In der verfügbaren Datenquelle wurde kein OE-Treffer gefunden. Sie können eine Teileanfrage senden.'
          );
        } else {
          this.showMessage(
            this.elements.partMessage,
            error.message || 'Die Teilesuche konnte nicht abgeschlossen werden.'
          );
        }
      } finally {
        this.setBusy(this.elements.categorySearch, false);
      }
    }

    selectGroup(group, button) {
      this.selectedGroup = group;
      this.elements.categoryList.querySelectorAll('.spf-category-option').forEach((option) => {
        const isSelected = option === button;
        option.setAttribute('aria-pressed', String(isSelected));
        const state = option.querySelector('.spf-category-state');
        if (state) state.textContent = isSelected ? '✓ Ausgewählt' : 'Auswählen';
      });
      this.elements.productsSubmit.hidden = false;
    }

    async searchProducts() {
      if (this.busy || !this.vehicle || !this.selectedGroup) return;
      this.clearMessage(this.elements.partMessage);
      this.setBusy(this.elements.productsSubmit, true, 'Produkte werden geprüft …');
      try {
        const result = await this.api('/search', {
          vehicleId: this.vehicle.resolved.vehicleId,
          vehicle: {
            manufacturer: this.vehicle.resolved.manufacturer,
            model: this.vehicle.resolved.model,
            engine: this.vehicle.resolved.engine,
            engineCodes: this.vehicle.resolved.engineCodes || []
          },
          categoryId: this.selectedGroup.categoryId,
          productId: this.selectedGroup.productId,
          partGroupLabel: this.selectedGroup.label,
          partQuery: this.elements.partQuery.value.trim()
        });
        this.renderProducts(result.products);
        this.showMessage(this.elements.resultsMessage, result.message || 'Bitte prüfen Sie Motor, Baujahr und Einbauhinweise vor dem Kauf.', 'info');
      } catch (error) {
        if (error.status === 'no_shopify_match' || error.status === 'no_tecdoc_match') {
          this.renderProducts([]);
          this.showMessage(
            this.elements.resultsMessage,
            'In der verfügbaren Datenquelle wurde kein OE-Treffer gefunden. Sie können eine Teileanfrage senden.'
          );
        } else {
          this.showMessage(
            this.elements.partMessage,
            error.message || 'Die Produktsuche konnte nicht abgeschlossen werden.'
          );
        }
      } finally {
        this.setBusy(this.elements.productsSubmit, false);
      }
    }

    renderProducts(products) {
      this.hasSearchResult = true;
      this.elements.productGrid.replaceChildren();
      this.clearMessage(this.elements.resultsMessage);
      if (this.elements.contactCard) {
        this.elements.contactCard.hidden = products.length > 0;
      }
      if (products.length === 0) this.populateContactRequest();
      products.forEach((product) => {
        const card = document.createElement('article');
        card.className = 'spf-product-card';
        const imageWrap = document.createElement('div');
        imageWrap.className = 'spf-product-image';
        if (product.imageUrl) {
          const image = document.createElement('img');
          image.src = product.imageUrl;
          image.alt = `${this.localizedPartName(product.verifiedPartName)} ${product.sku || ''}`.trim();
          image.loading = 'lazy';
          imageWrap.append(image);
        }
        const body = document.createElement('div');
        body.className = 'spf-product-body';
        const title = document.createElement('h3');
        title.textContent = product.title || this.localizedPartName(product.verifiedPartName);
        const sku = document.createElement('span');
        sku.className = 'spf-product-sku';
        sku.textContent = [
          product.vendor,
          product.sku ? `SKU: ${product.sku}` : product.variantTitle
        ].filter(Boolean).join(' · ');
        const status = document.createElement('span');
        status.className = 'spf-product-status';
        status.textContent = product.fitmentVerified === false ? `OE-Referenz stimmt überein · ${product.verificationLabel}` : `✓ Verifiziert · ${product.verificationLabel}`;
        const price = document.createElement('strong');
        price.className = 'spf-product-price';
        price.textContent = new Intl.NumberFormat('de-DE', {
          style: 'currency',
          currency: 'EUR'
        }).format(Number(product.price));
        const link = document.createElement('a');
        link.className = 'spf-product-link';
        link.href = product.productUrl;
        link.textContent = 'Produkt ansehen';
        body.append(title, sku, status);
        if (product.fitmentVerified === false) {
          const warning = document.createElement('p');
          warning.className = 'spf-fitment-warning';
          warning.textContent = 'Fahrzeugausführung vor dem Kauf prüfen: Motor, Baujahr, Abgasnorm und Einbauposition.';
          body.append(warning);
          const details = document.createElement('details');
          const summary = document.createElement('summary');
          summary.textContent = 'Zuordnung und Einbauhinweise ansehen';
          details.append(summary);
          for (const note of product.fitmentNotes || []) {
            const text = document.createElement('p');
            text.textContent = note;
            details.append(text);
          }
          for (const url of product.sourceUrls || []) {
            if (new URL(url).hostname !== 'rema-parts.de') continue;
            const source = document.createElement('a');
            source.href = url;
            source.target = '_blank';
            source.rel = 'noopener noreferrer';
            source.textContent = 'Quelle: Rema Parts';
            details.append(source);
          }
          body.append(details);
        }
        body.append(price, link);
        card.append(imageWrap, body);
        this.elements.productGrid.append(card);
      });
      this.elements.vehicleStep.hidden = true;
      this.elements.confirmationStep.hidden = true;
      this.elements.partStep.hidden = true;
      this.elements.resultsStep.hidden = false;
      this.setProgress(4);
    }

    populateContactRequest() {
      if (!this.vehicle?.resolved) return;
      const resolved = this.vehicle.resolved;
      const identifiers = [];
      if (this.vehicle.hsn && this.vehicle.tsn) {
        identifiers.push(`HSN / TSN: ${this.vehicle.hsn} / ${this.vehicle.tsn}`);
      }
      const details = [
        `Fahrzeug: ${resolved.manufacturer} ${resolved.model}`,
        resolved.engine ? `Motorisierung: ${resolved.engine}` : '',
        ...identifiers
      ].filter(Boolean);
      const requestedPart = this.elements.partQuery.value.trim();
      if (this.elements.contactVehicle) {
        this.elements.contactVehicle.value = details.join(' | ');
      }
      if (this.elements.contactPart) {
        this.elements.contactPart.value = requestedPart;
      }
    }

    restoreContactSubmissionState() {
      const state = this.root.dataset.spfContactState;
      if (state !== 'success' && state !== 'error') return;

      this.hasSearchResult = true;
      this.currentStep = 4;
      this.maxReachedStep = 4;
      this.elements.vehicleStep.hidden = true;
      this.elements.confirmationStep.hidden = true;
      this.elements.partStep.hidden = true;
      this.elements.resultsStep.hidden = false;
      if (this.elements.contactCard) this.elements.contactCard.hidden = false;
      this.syncProgress();
      this.elements.contactCard?.scrollIntoView({
        block: 'center',
        behavior: 'smooth'
      });
    }

    localizedPartName(partName) {
      const normalized = String(partName || '').toUpperCase();
      if (normalized.includes('SOOT/PARTICULATE FILTER')) {
        return 'DPF / Dieselpartikelfilter';
      }
      if (normalized.includes('CATALYTIC CONVERTER')) {
        return 'Katalysator';
      }
      if (normalized.includes('EXHAUST SYSTEM')) {
        return 'Abgasanlage';
      }
      if (normalized === 'EXHAUST PIPES') {
        return 'Auspuffrohre';
      }
      if (normalized.includes('EXHAUST PIPE')) {
        return 'Auspuffrohr';
      }
      if (normalized.includes('BRAKE SYSTEM')) {
        return 'Bremsanlage';
      }
      if (normalized.includes('BRAKE PAD')) {
        return 'Bremsbeläge';
      }
      if (normalized.includes('BRAKE DISC')) {
        return 'Bremsscheiben';
      }
      if (normalized.includes('WIPER BLADE')) {
        return 'Scheibenwischer';
      }
      return partName || this.selectedGroup?.label || 'Ersatzteil';
    }

    navigateToStep(step) {
      if (this.busy || !this.isStepAvailable(step)) return;

      if (step === 1) {
        this.elements.confirmationStep.hidden = true;
        this.elements.partStep.hidden = true;
        this.elements.resultsStep.hidden = true;
        this.elements.vehicleStep.hidden = false;
        this.setProgress(1);
        this.elements.vehicleSubmit.focus();
        return;
      }

      if (step === 2) {
        this.openConfirmationStep();
        return;
      }

      if (step === 3) {
        this.openPartStep({ focus: false });
        return;
      }

      this.elements.vehicleStep.hidden = true;
      this.elements.confirmationStep.hidden = true;
      this.elements.partStep.hidden = true;
      this.elements.resultsStep.hidden = false;
      this.setProgress(4);
    }

    isStepAvailable(step) {
      if (step === 1) return true;
      if (step === 2) return Boolean(this.pendingVehicle || this.vehicle);
      if (step === 3) return Boolean(this.vehicle);
      if (step === 4) return Boolean(this.vehicle && this.hasSearchResult);
      return false;
    }

    setProgress(step) {
      this.currentStep = step;
      this.maxReachedStep = Math.max(this.maxReachedStep, step);
      this.syncProgress();
    }

    syncProgress() {
      this.elements.progressItems.forEach((item) => {
        const step = Number(item.dataset.spfProgress);
        const isCurrent = step === this.currentStep;
        item.disabled = this.busy || !this.isStepAvailable(step);
        item.classList.toggle('is-current', isCurrent);
        item.classList.toggle(
          'is-complete',
          !isCurrent && step < this.maxReachedStep
        );
        if (isCurrent) {
          item.setAttribute('aria-current', 'step');
        } else {
          item.removeAttribute('aria-current');
        }
      });
      this.elements.progressLines.forEach((line, index) => {
        line.classList.toggle('is-complete', this.maxReachedStep > index + 1);
      });
    }

    saveGarage() {
      try {
        localStorage.setItem(GARAGE_KEY, JSON.stringify(this.vehicle));
        this.renderGarage(this.vehicle);
      } catch {
        // The verified search continues even when browser storage is unavailable.
      }
    }

    restoreGarage() {
      try {
        const stored = JSON.parse(
          localStorage.getItem(GARAGE_KEY) ||
            localStorage.getItem(LEGACY_GARAGE_KEY) ||
            'null'
        );
        const hasIdentifiers = Boolean(stored?.hsn && stored?.tsn);
        if (hasIdentifiers && typeof stored?.resolved?.vehicleId === 'string' && stored.resolved.vehicleId.includes('.')) {
          if (stored.tsn && !stored.tsnLookup) {
            stored.tsnLookup = stored.tsn.slice(0, 3);
          }
          const officialEngineCodes =
            stored.resolved.engineCodes?.filter(Boolean) || [];
          if (!stored.engineCode && officialEngineCodes.length === 1) {
            stored.engineCode = officialEngineCodes[0];
          }
          delete stored.vin;
          delete stored.typeVariantVersion;
          localStorage.setItem(GARAGE_KEY, JSON.stringify(stored));
          localStorage.removeItem(LEGACY_GARAGE_KEY);
          this.renderGarage(stored);
        } else {
          this.renderEmptyGarage();
        }
      } catch {
        localStorage.removeItem(GARAGE_KEY);
        this.renderEmptyGarage();
      }
    }

    renderEmptyGarage() {
      this.garageVehicle = null;
      this.elements.garageName.textContent = '';
      this.elements.garageMeta.textContent = '';
      this.elements.useGarage.hidden = true;
      this.elements.removeGarage.hidden = true;
      this.elements.garage.hidden = true;
      this.elements.garageToggle.hidden = true;
    }

    renderGarage(vehicle) {
      this.garageVehicle = vehicle;
      this.elements.garageName.textContent = `${vehicle.resolved.manufacturer} ${vehicle.resolved.model}`;
      this.elements.garageMeta.textContent = [
        vehicle.resolved.engine,
        vehicle.hsn && vehicle.tsn ? `HSN ${vehicle.hsn} / TSN ${vehicle.tsn}` : ''
      ].filter(Boolean).join(' · ');
      this.elements.useGarage.hidden = false;
      this.elements.removeGarage.hidden = false;
      this.elements.garage.hidden = false;
      this.elements.garageToggle.hidden = false;
    }

    useGarage() {
      if (!this.garageVehicle) return;
      this.pendingVehicle = null;
      this.vehicle = this.garageVehicle;
      this.elements.hsn.value = this.vehicle.hsn || '';
      this.elements.tsn.value = this.vehicle.tsn || '';
      const officialEngineCodes =
        this.vehicle.resolved.engineCodes?.filter(Boolean) || [];
      const garageEngineCode =
        this.vehicle.engineCode ||
        (officialEngineCodes.length === 1 ? officialEngineCodes[0] : '');
      this.vehicle.engineCode = garageEngineCode || null;
      this.elements.engineCode.value = garageEngineCode || '';
      this.ocrEngineCodeHint = garageEngineCode || null;
      this.resolveVehicle();
    }

    editVerifiedVehicle() {
      this.pendingVehicle = null;
      this.vehicle = null;
      this.selectedGroup = null;
      this.ocrEngineCodeHint = null;
      this.hasSearchResult = false;
      this.clearDownstreamResults();
      this.maxReachedStep = 1;
      this.elements.confirmationStep.hidden = true;
      this.elements.partStep.hidden = true;
      this.elements.resultsStep.hidden = true;
      this.elements.vehicleStep.hidden = false;
      this.clearMessage(this.elements.vehicleMessage);
      this.setProgress(1);
      this.updateVehicleButton();
    }

    removeGarage() {
      localStorage.removeItem(GARAGE_KEY);
      localStorage.removeItem(LEGACY_GARAGE_KEY);
      this.renderEmptyGarage();
    }

    resetVehicle() {
      this.pendingVehicle = null;
      this.vehicle = null;
      this.selectedGroup = null;
      this.ocrPendingConfirmation = false;
      this.hasSearchResult = false;
      this.currentStep = 1;
      this.maxReachedStep = 1;
      this.removeSelectedFile();
      [
        this.elements.hsn,
        this.elements.tsn,
        this.elements.engineCode
      ].forEach((input) => {
        input.value = '';
      });
      this.elements.confirmationNotice.hidden = true;
      this.elements.confirmationStep.hidden = true;
      this.elements.partStep.hidden = true;
      this.elements.resultsStep.hidden = true;
      this.elements.vehicleStep.hidden = false;
      this.clearMessage(this.elements.vehicleMessage);
      this.clearMessage(this.elements.confirmationMessage);
      this.setProgress(1);
      this.updateVehicleButton();
    }

    resetPartSearch() {
      this.selectedGroup = null;
      this.hasSearchResult = false;
      this.maxReachedStep = 3;
      this.elements.partQuery.value = '';
      this.elements.categoryList.replaceChildren();
      this.elements.categoryResults.hidden = true;
      this.elements.productsSubmit.hidden = true;
      this.elements.confirmationStep.hidden = true;
      this.elements.resultsStep.hidden = true;
      this.elements.partStep.hidden = false;
      this.clearMessage(this.elements.partMessage);
      this.setProgress(3);
      this.elements.partQuery.focus();
    }
  }

  const mount = () => {
    document.querySelectorAll('[data-samsuns-parts-finder]').forEach((root) => {
      if (!root.dataset.spfMounted) {
        root.dataset.spfMounted = 'true';
        new SamsunsPartsFinder(root);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount, { once: true });
  } else {
    mount();
  }
})();

